import React, { useState } from "react";
import { Box, Button, TextField, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import MplusApi from "Services/Mplus";
import {
  FINANCING_STATUS,
  apiErrorMessage,
  formatAmount,
  formatDate,
  formatDateTime,
  todayIso,
  unwrap,
  useHasRole,
} from "./mplusUtils";

const useStyles = makeStyles(() => ({
  grid: { display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 14, "@media (max-width: 900px)": { gridTemplateColumns: "1fr" } },
  block: { minWidth: 0, padding: 14, border: "1px solid #e6edf1", borderRadius: 8, background: "#fbfcfd" },
  blockTitle: { marginBottom: 8, color: "#1d3047", fontSize: "14px !important", fontWeight: "800 !important" },
  row: { display: "flex", justifyContent: "space-between", gap: 10, padding: "6px 0", borderBottom: "1px solid #eef2f5", color: "#63748a", fontSize: 12.5, "&:last-child": { borderBottom: 0 }, "& b": { color: "#30465d", textAlign: "right", overflowWrap: "anywhere" } },
  badge: { padding: "2px 8px", borderRadius: 4, fontSize: 11.5, fontWeight: 700, whiteSpace: "nowrap" },
  danger: { color: "#b42318", background: "#fde2e0" },
  ok: { color: "#08734e", background: "#dff3e7" },
  pending: { color: "#8d5200", background: "#ffe9b8" },
  pauseForm: { display: "flex", alignItems: "center", gap: 8, marginTop: 10, flexWrap: "wrap", "& .MuiInputBase-root": { fontSize: 12, height: 34 } },
  action: { color: "#087b59 !important", textTransform: "none", fontSize: "12px !important", fontWeight: "700 !important", padding: "4px 6px" },
  empty: { color: "#8a97a6", fontSize: "12px !important" },
}));

const locationName = (location) => (location ? [location.name || location.code, location.city].filter(Boolean).join(" · ") : "--");

const DOCUMENT_LABELS = {
  CARTE_GRISE: "Carte grise",
  ASSURANCE: "Assurance",
  CONTRAT: "Contrat",
  CLES_RECHANGE: "Clés de rechange",
  MANUEL: "Manuel du véhicule",
};
export const documentLabel = (code) => DOCUMENT_LABELS[code] || code;

// Synthèse d'un dossier M+ (section 6) : étapes clés, suivi, véhicule et remise.
const DossierDetails = ({ financing, notify, onReload }) => {
  const classes = useStyles();
  const hasRole = useHasRole();
  const [pauseOpen, setPauseOpen] = useState(false);
  const [until, setUntil] = useState("");
  const [busy, setBusy] = useState(false);

  if (!financing) return null;
  const { asset, delivery } = financing;
  const arrears = financing.arrears_amount;
  const pausedUntil = financing.collection_paused_until;
  const paused = pausedUntil && String(pausedUntil).slice(0, 10) >= todayIso();
  const canPause = hasRole("FINANCE") && financing.status === "ACTIVE";

  const submitPause = async (value) => {
    setBusy(true);
    try {
      unwrap(await MplusApi.runFinancingAction(financing.id, "pause", { until: value }));
      notify(value ? `Prélèvements suspendus jusqu'au ${formatDate(value)}.` : "Prélèvements repris.", "success");
      setPauseOpen(false);
      onReload();
    } catch (error) {
      notify(apiErrorMessage(error, "Impossible de modifier la suspension."), "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box className={classes.grid}>
      <Box className={classes.block}>
        <Typography className={classes.blockTitle}>Dossier {financing.reference || ""}</Typography>
        <Box className={classes.row}><span>Statut</span><b>{FINANCING_STATUS[financing.status]?.label || financing.status || "--"}</b></Box>
        <Box className={classes.row}><span>Client et portefeuille Fineract prêts</span><b>{financing.setup_done == null ? "--" : financing.setup_done ? "Oui" : "Non"}</b></Box>
        <Box className={classes.row}><span>Création du prêt</span><b>{formatDate(financing.loan_created_at)}</b></Box>
        <Box className={classes.row}><span>Approbation du prêt</span><b>{formatDate(financing.loan_approved_at)}</b></Box>
        <Box className={classes.row}><span>Livraison prévue</span><b>{formatDate(financing.planned_delivery_date)}{financing.delivery_location ? ` · ${locationName(financing.delivery_location)}` : ""}</b></Box>
        <Box className={classes.row}><span>Livraison réelle</span><b>{formatDate(financing.delivered_at)}</b></Box>
      </Box>

      <Box className={classes.block}>
        <Typography className={classes.blockTitle}>Suivi des prélèvements</Typography>
        <Box className={classes.row}>
          <span>Arriérés</span>
          {arrears == null ? <b>--</b> : <span className={`${classes.badge} ${Number(arrears) > 0 ? classes.danger : classes.ok}`}>{formatAmount(arrears)}</span>}
        </Box>
        <Box className={classes.row}>
          <span>Prélèvements</span>
          <span className={`${classes.badge} ${paused ? classes.pending : classes.ok}`}>{paused ? `Suspendus jusqu'au ${formatDate(pausedUntil)}` : "Actifs"}</span>
        </Box>
        {canPause && (paused ? (
          <Button className={classes.action} disabled={busy} onClick={() => submitPause(null)}>Reprendre les prélèvements</Button>
        ) : pauseOpen ? (
          <Box className={classes.pauseForm}>
            <TextField type="date" size="small" value={until} inputProps={{ min: todayIso() }} onChange={(event) => setUntil(event.target.value)} />
            <Button className={classes.action} disabled={!until || busy} onClick={() => submitPause(until)}>Suspendre</Button>
            <Button className={classes.action} onClick={() => setPauseOpen(false)}>Annuler</Button>
          </Box>
        ) : (
          <Button className={classes.action} onClick={() => setPauseOpen(true)}>Suspendre temporairement</Button>
        ))}
      </Box>

      <Box className={classes.block}>
        <Typography className={classes.blockTitle}>{delivery ? "Remise du véhicule" : "Véhicule"}</Typography>
        {delivery ? (
          <>
            <Box className={classes.row}><span>Date</span><b>{formatDate(delivery.date)}</b></Box>
            <Box className={classes.row}><span>Hub</span><b>{locationName(delivery.hub)}</b></Box>
            <Box className={classes.row}><span>VIN</span><b>{delivery.vin || "--"}</b></Box>
            <Box className={classes.row}><span>Plaque</span><b>{delivery.plate || "--"}</b></Box>
            <Box className={classes.row}><span>Documents remis</span><b>{(delivery.documents_handed || []).map(documentLabel).join(", ") || "--"}</b></Box>
            {delivery.note && <Box className={classes.row}><span>Note</span><b>{delivery.note}</b></Box>}
          </>
        ) : asset ? (
          <>
            <Box className={classes.row}><span>VIN</span><b>{asset.vin || "--"}</b></Box>
            <Box className={classes.row}><span>Plaque</span><b>{asset.plate || "--"}</b></Box>
            <Box className={classes.row}><span>Couleur</span><b>{asset.color || "--"}</b></Box>
            <Box className={classes.row}><span>Kilométrage</span><b>{asset.odometer_km == null ? "--" : `${asset.odometer_km} km`}</b></Box>
            <Box className={classes.row}><span>Statut</span><b>{asset.status || "--"}</b></Box>
            <Box className={classes.row}><span>Emplacement</span><b>{locationName(asset.location)}</b></Box>
            <Box className={classes.row}><span>Réservé jusqu'au</span><b>{formatDateTime(asset.reserved_until)}</b></Box>
          </>
        ) : (
          <Typography className={classes.empty}>Aucun véhicule réservé pour ce dossier.</Typography>
        )}
      </Box>
    </Box>
  );
};

export default DossierDetails;
